import React, { useState } from 'react';
import { ec as EC } from 'elliptic';
import bigInt from 'big-integer';

const ec = new EC('curve25519');

const ECDHKeyExchangeSimulation = () => {
  const [curve, setCurve] = useState({ a: 486662, b: 1, p: bigInt(2).pow(255).subtract(19) });
  const [privateKeyA, setPrivateKeyA] = useState(null);
  const [publicKeyA, setPublicKeyA] = useState(null);
  const [privateKeyB, setPrivateKeyB] = useState(null);
  const [publicKeyB, setPublicKeyB] = useState(null);
  const [secretKeyA, setSecretKeyA] = useState(null);
  const [secretKeyB, setSecretKeyB] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);

  const generatePointG = async () => {
    try {
      const privateKeyA = bigInt.randBetween(1, curve.p);
      const ellipticPrivateKeyA = ec.keyFromPrivate(privateKeyA.toString());
      const keyPairA = ellipticPrivateKeyA.getPublic();

      setPrivateKeyA(privateKeyA);
      setPublicKeyA(keyPairA);

      const privateKeyB = bigInt.randBetween(1, curve.p);
      const ellipticPrivateKeyB = ec.keyFromPrivate(privateKeyB.toString());
      const keyPairB = ellipticPrivateKeyB.getPublic();

      setPrivateKeyB(privateKeyB);
      setPublicKeyB(keyPairB);

      const secretKeyA = ellipticPrivateKeyB.derive(ellipticPrivateKeyA.getPublic()).toArray();
      const secretKeyB = ellipticPrivateKeyA.derive(keyPairB).toArray();

      setSecretKeyA(secretKeyA);
      setSecretKeyB(secretKeyB);
    } catch (error) {
      console.error('Error generating key pair:', error);
    }
  };

  const handleStartSimulation = async () => {
    setCurrentStep(1);
    await generatePointG();
  };

  const handleNextStep = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setPrivateKeyA(null);
    setPublicKeyA(null);
    setPrivateKeyB(null);
    setPublicKeyB(null);
    setSecretKeyA(null);
    setSecretKeyB(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">ECDH Key Exchange Simulation</h1>
        
        <div className="grid grid-cols-4 gap-4">
          {/* Left Panel - Explanation */}
          <div className="col-span-1">
            <div className="bg-white p-4 rounded-lg shadow">
              <h2 className="text-lg font-bold mb-3">Explanation</h2>
              <div className="space-y-3 text-sm">
                <p>
                  In Elliptic Curve Diffie-Hellman (ECDH), the base point (G) is a fixed point on the curve. For Curve25519, its
                  x-coordinate is 9.
                </p>
                <p>
                  The provided values are a common representation of the x-coordinate of the base point (G).
                </p>
                <p>
                  In ECDH, secret keys are calculated by multiplying a private key with the public key of the other user.
                  This shared secret key can be used for secure communication.
                </p>
              </div>
            </div>
          </div>

          {/* Right Panel - Content */}
          <div className="col-span-3 space-y-3">
            {/* Step Indicator */}
            <div className="bg-blue-100 p-3 rounded-lg">
              <p className="text-lg font-semibold text-blue-900">Step {currentStep} of 5</p>
            </div>

            {/* Step 1 */}
            {currentStep >= 1 && (
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="font-bold mb-2">Step 1: Elliptic Curve Parameters</h3>
                <p className="text-sm">a: {curve.a}, b: {curve.b}</p>
                <p className="text-sm">p: {curve.p.toString()} (2<sup>255</sup>-19)</p>
                <p className="text-xs text-gray-600 mt-2"><strong>Note:</strong> Curve25519 uses 256-bit (32-byte) keys for high security.</p>
              </div>
            )}

            {/* Step 2 */}
            {currentStep >= 2 && (
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="font-bold mb-2">Step 2: User A</h3>
                <p className="text-sm mb-2">1. Generate a secure random private key (nA)</p>
                <p className="text-sm mb-2">2. Convert the private key to elliptic format</p>
                <p className="text-sm mb-2">3. Calculate the public key (PA)</p>
                <p className="text-sm font-mono">Private Key (nA): {privateKeyA?.toString()}</p>
                <p className="text-sm font-mono">Public Key (PA): {publicKeyA ? publicKeyA.getX().toString() : 'Calculating...'}</p>
                <p className="text-xs text-gray-600 mt-2"><strong>Key Size:</strong> 256-bit private key, 256-bit public key x-coordinate</p>
              </div>
            )}

            {/* Step 3 */}
            {currentStep >= 3 && (
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="font-bold mb-2">Step 3: User B</h3>
                <p className="text-sm mb-2">1. Generate a secure random private key (nB)</p>
                <p className="text-sm mb-2">2. Calculate the public key (PB)</p>
                <p className="text-sm font-mono">Private Key (nB): {privateKeyB?.toString()}</p>
                <p className="text-sm font-mono">Public Key (PB): {publicKeyB ? publicKeyB.getX().toString() : 'Calculating...'}</p>
                <p className="text-xs text-gray-600 mt-2"><strong>Key Size:</strong> 256-bit private key, 256-bit public key x-coordinate</p>
              </div>
            )}

            {/* Step 4 */}
            {currentStep >= 4 && (
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="font-bold mb-2">Step 4: Secret Key of User A</h3>
                <p className="text-sm font-mono">Secret Key (K): {secretKeyA ? secretKeyA.toString() : 'Calculating...'}</p>
                <p className="text-xs text-gray-600 mt-2"><strong>Key Size:</strong> Derived 256-bit shared secret from private key and peer's public key</p>
              </div>
            )}

            {/* Step 5 */}
            {currentStep >= 5 && (
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="font-bold mb-2">Step 5: Secret Key of User B</h3>
                <p className="text-sm font-mono">Secret Key (K): {secretKeyB ? secretKeyB.toString() : 'Calculating...'}</p>
                <p className="text-xs text-gray-600 mt-2"><strong>Key Size:</strong> Derived 256-bit shared secret from private key and peer's public key</p>
              </div>
            )}

            {/* Base Point Info */}
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="font-bold mb-2">Elliptic Curve Base Point (G)</h3>
              <p className="text-sm font-mono break-all">G (hex): {ec.g.encodeCompressed('hex')}</p>
            </div>

            {/* Controls */}
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="font-bold mb-3">Controls</h3>
              <div className="space-x-4 flex-wrap">
                <button
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
                  onClick={handleStartSimulation}
                  disabled={currentStep > 0}
                >
                  Start Simulation
                </button>
                <button
                  className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
                  onClick={handleNextStep}
                  disabled={currentStep === 0 || currentStep === 5}
                >
                  Next Step
                </button>
                <button
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-4 rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
                  onClick={handlePreviousStep}
                  disabled={currentStep <= 0}
                >
                  Previous Step
                </button>
                <button
                  className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded"
                  onClick={handleReset}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ECDHKeyExchangeSimulation;
